/**
 * concours.ts — pure helpers for the Modifications & Concours Impact feature.
 *
 * HONESTY CONTRACT (spec E5): concours impact is the owner's self-assessment,
 * NOT a certified score. No number/percentage/points is ever stored or rendered.
 * The roll-up is derived at render time only — never written back to Firestore.
 */

import type { ConcoursImpact, Modification } from '@/types/domain';

// Severity order: none < minor < moderate < major.
const SEVERITY: Record<ConcoursImpact, number> = {
  none: 0,
  minor: 1,
  moderate: 2,
  major: 3,
};

export interface ConcoursRollup {
  /** Worst-case impact rung across all modifications. 'none' when empty. */
  worst: ConcoursImpact;
  /** Total number of modifications logged (regardless of impact rung). */
  count: number;
}

/**
 * Derive the concours roll-up for a vehicle's modification list.
 *
 * Pure function — no side effects, no Firestore access. Called at render time
 * only. The result MUST NOT be persisted to any Firestore document.
 *
 * @param mods - The full modification list for a vehicle.
 * @returns `{ worst, count }` — worst-case rung + total count.
 *
 * @example
 *   deriveConcoursRollup([]) // → { worst: 'none', count: 0 }
 *   deriveConcoursRollup([{ concoursImpact: 'minor' }, { concoursImpact: 'major' }])
 *   // → { worst: 'major', count: 2 }
 */
export function deriveConcoursRollup(mods: Modification[]): ConcoursRollup {
  if (mods.length === 0) {
    return { worst: 'none', count: 0 };
  }

  let worstSeverity = 0;
  let worst: ConcoursImpact = 'none';

  for (const mod of mods) {
    const sev = SEVERITY[mod.concoursImpact] ?? 0;
    if (sev > worstSeverity) {
      worstSeverity = sev;
      worst = mod.concoursImpact;
    }
  }

  return { worst, count: mods.length };
}
